import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, MessageSquare, Globe, Sun, Moon } from 'lucide-react';
import { t, getLanguageName } from '../utils/translation';
import { isAdminAuthenticated } from '../services/blogService';
import logoImg from '../assets/logo.png';

const Header = ({ theme, toggleTheme, language, changeLanguage, translating }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isAdmin] = useState(isAdminAuthenticated());
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const menuRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsLanguageOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsLanguageOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'no', name: 'Norsk', flag: '🇳🇴' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'ko', name: '한국어', flag: '🇰🇷' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  ];

  const currentLang = languages.find(l => l.code === language) || languages[0];

  const navLinks = [
    { to: '/', label: t('home', language) },
    { to: '/about', label: t('about', language) },
    { to: '/services', label: t('services', language) },
    { to: '/industries', label: t('industries', language) },
    { to: '/manufacturing', label: t('manufacturing', language) },
    { to: '/blog', label: t('blog', language) },
    { to: '/contact', label: t('contact', language) },
  ];

  const isDark = theme === 'dark';

  const tickerItems = [
    { icon: <Phone style={{ width: 10, height: 10, flexShrink: 0 }} />, text: '+86 180 1328 9913' },
    { icon: <Mail style={{ width: 10, height: 10, flexShrink: 0 }} />, text: 'tony@qsconn.com' },
    { icon: <MessageSquare style={{ width: 10, height: 10, flexShrink: 0 }} />, text: 'WeChat: 18013289913' },
    { icon: <MessageSquare style={{ width: 10, height: 10, flexShrink: 0 }} />, text: 'WhatsApp: +86 139 1322 5243' },
    { icon: <MapPin style={{ width: 10, height: 10, flexShrink: 0 }} />, text: t('factoryAddress', language) },
  ];

  return (
    <>
      <header
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          transition: 'all 0.3s ease',
          background: isDark
            ? scrolled ? 'rgba(0,0,0,0.95)' : 'rgba(0,0,0,0.85)'
            : scrolled ? 'rgba(255,255,255,0.97)' : 'rgba(255,255,255,0.90)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: isDark
            ? scrolled ? '1px solid rgba(220,38,38,0.2)' : '1px solid rgba(255,255,255,0.05)'
            : scrolled ? '1px solid rgba(220,38,38,0.15)' : '1px solid rgba(0,0,0,0.08)',
          boxShadow: scrolled
            ? isDark ? '0 4px 30px rgba(0,0,0,0.5)' : '0 4px 30px rgba(0,0,0,0.08)'
            : 'none',
        }}
      >
        {/* Top contact bar — desktop */}
        <div
          style={{
            background: isDark ? 'rgba(220,38,38,0.08)' : 'rgba(220,38,38,0.05)',
            borderBottom: isDark ? '1px solid rgba(220,38,38,0.12)' : '1px solid rgba(220,38,38,0.08)',
          }}
          className="hidden md:block"
        >
          <div className="max-w-7xl mx-auto px-4 py-1.5">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-5">
                <a href="tel:+8618013289913" className="flex items-center gap-1.5 text-xs transition-colors"
                  style={{ color: isDark ? '#9ca3af' : '#6b7280', minHeight: 'unset', minWidth: 'unset' }}>
                  <Phone style={{ width: 11, height: 11 }} />
                  <span>+86 180 1328 9913</span>
                </a>
                <a href="mailto:tony@qsconn.com" className="flex items-center gap-1.5 text-xs transition-colors"
                  style={{ color: isDark ? '#9ca3af' : '#6b7280', minHeight: 'unset', minWidth: 'unset' }}>
                  <Mail style={{ width: 11, height: 11 }} />
                  <span>tony@qsconn.com</span>
                </a>
              </div>
              <div className="flex items-center gap-5">
                <span className="flex items-center gap-1.5 text-xs"
                  style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                  <MessageSquare style={{ width: 11, height: 11 }} />
                  <span>WeChat: 18013289913</span>
                </span>
                <span className="flex items-center gap-1.5 text-xs"
                  style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                  <MessageSquare style={{ width: 11, height: 11 }} />
                  <span>WhatsApp: +86 139 1322 5243</span>
                </span>
                <span className="flex items-center gap-1.5 text-xs"
                  style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                  <MapPin style={{ width: 11, height: 11 }} />
                  <span>{t('factoryAddress', language)}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Top contact bar — mobile scrolling ticker */}
        <div
          className="md:hidden"
          style={{
            background: isDark
              ? 'linear-gradient(90deg, rgba(220,38,38,0.12), rgba(220,38,38,0.06), rgba(220,38,38,0.12))'
              : 'linear-gradient(90deg, rgba(220,38,38,0.07), rgba(220,38,38,0.03), rgba(220,38,38,0.07))',
            borderBottom: isDark ? '1px solid rgba(220,38,38,0.15)' : '1px solid rgba(220,38,38,0.1)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* fade masks */}
          <div style={{
            position: 'absolute', top: 0, left: 0, bottom: 0, width: 32, zIndex: 2,
            background: isDark
              ? 'linear-gradient(90deg, rgba(0,0,0,0.85), transparent)'
              : 'linear-gradient(90deg, rgba(255,255,255,0.9), transparent)',
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', top: 0, right: 0, bottom: 0, width: 32, zIndex: 2,
            background: isDark
              ? 'linear-gradient(270deg, rgba(0,0,0,0.85), transparent)'
              : 'linear-gradient(270deg, rgba(255,255,255,0.9), transparent)',
            pointerEvents: 'none',
          }} />
          <div style={{ display: 'flex', animation: 'mobileTicker 28s linear infinite', width: 'max-content', padding: '5px 0' }}>
            {[...tickerItems, ...tickerItems].map((item, i) => (
              <span key={i} style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 10.5,
                fontWeight: 500,
                color: isDark ? '#9ca3af' : '#6b7280',
                paddingRight: 28,
                whiteSpace: 'nowrap',
              }}>
                <span style={{ color: '#dc2626' }}>{item.icon}</span>
                {item.text}
                <span style={{ color: isDark ? 'rgba(220,38,38,0.4)' : 'rgba(220,38,38,0.3)', marginLeft: 12 }}>◆</span>
              </span>
            ))}
          </div>
        </div>

        {/* Main nav bar */}
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center" style={{ height: 64 }}>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5" style={{ minHeight: 'unset', minWidth: 'unset' }}>
              <img
                src={logoImg}
                alt="QingShun Electronics"
                style={{
                  height: 42,
                  width: 'auto',
                  flexShrink: 0,
                  filter: isDark ? 'brightness(1.1) drop-shadow(0 2px 8px rgba(220,38,38,0.3))' : 'drop-shadow(0 2px 6px rgba(0,0,0,0.1))',
                }}
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map(({ to, label }) => {
                const active = location.pathname === to;
                return (
                  <Link
                    key={to}
                    to={to}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 8,
                      fontSize: 13.5,
                      fontWeight: active ? 700 : 500,
                      color: active ? '#dc2626' : isDark ? '#d1d5db' : '#374151',
                      background: active
                        ? isDark ? 'rgba(220,38,38,0.12)' : 'rgba(220,38,38,0.08)'
                        : 'transparent',
                      transition: 'all 0.2s',
                      textDecoration: 'none',
                      position: 'relative',
                      minHeight: 'unset',
                      minWidth: 'unset',
                    }}
                    className="nav-link-hover"
                  >
                    {label}
                    {active && (
                      <span style={{
                        position: 'absolute',
                        bottom: -2,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 16,
                        height: 2,
                        borderRadius: 999,
                        background: '#dc2626',
                      }} />
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Desktop Controls */}
            <div className="hidden md:flex items-center gap-2" ref={menuRef}>
              {/* Language Dropdown */}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '7px 12px',
                    borderRadius: 10,
                    border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                    background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 500,
                    color: isDark ? '#d1d5db' : '#374151',
                    transition: 'all 0.2s',
                    minHeight: 'unset',
                    minWidth: 'unset',
                  }}
                  aria-label="Select language"
                >
                  {translating ? (
                    <span style={{
                      width: 14, height: 14,
                      border: '2px solid rgba(220,38,38,0.3)',
                      borderTop: '2px solid #dc2626',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                      display: 'inline-block',
                      flexShrink: 0,
                    }} />
                  ) : (
                    <span style={{ fontSize: 15 }}>{currentLang.flag}</span>
                  )}
                  <span>{currentLang.name}</span>
                  <svg width="10" height="10" viewBox="0 0 10 10" style={{
                    transform: isLanguageOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                    opacity: 0.6,
                  }}>
                    <path d="M1 3l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                  </svg>
                </button>

                {isLanguageOpen && (
                  <div style={{
                    position: 'absolute',
                    right: 0,
                    top: 'calc(100% + 8px)',
                    width: 180,
                    borderRadius: 12,
                    border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                    background: isDark ? '#111' : '#fff',
                    boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.6)' : '0 20px 60px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                    animation: 'dropdownIn 0.15s ease',
                    zIndex: 100,
                  }}>
                    <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => { changeLanguage(lang.code); setIsLanguageOpen(false); }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            width: '100%',
                            padding: '9px 14px',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: language === lang.code ? 700 : 400,
                            color: language === lang.code ? '#dc2626' : isDark ? '#d1d5db' : '#374151',
                            background: language === lang.code
                              ? isDark ? 'rgba(220,38,38,0.12)' : 'rgba(220,38,38,0.07)'
                              : 'transparent',
                            textAlign: 'left',
                            transition: 'background 0.15s',
                            minHeight: 'unset',
                            minWidth: 'unset',
                          }}
                        >
                          <span style={{ fontSize: 16 }}>{lang.flag}</span>
                          {lang.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Desktop Theme Toggle */}
              <button
                onClick={toggleTheme}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '7px 12px',
                  borderRadius: 10,
                  border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                  background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                  cursor: 'pointer',
                  color: isDark ? '#9ca3af' : '#6b7280',
                  fontSize: 13,
                  fontWeight: 500,
                  transition: 'all 0.2s',
                  minHeight: 'unset',
                  minWidth: 'unset',
                }}
              >
                {isDark ? <Sun size={14} /> : <Moon size={14} />}
                <span>{isDark ? 'Light' : 'Dark'}</span>
              </button>

              {/* CTA Button */}
              <Link
                to="/contact"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, #dc2626, #991b1b)',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 700,
                  textDecoration: 'none',
                  boxShadow: '0 4px 12px rgba(220,38,38,0.3)',
                  transition: 'all 0.2s',
                  minHeight: 'unset',
                  minWidth: 'unset',
                  letterSpacing: 0.3,
                }}
              >
                {t('getAQuote', language)}
              </Link>
            </div>

            {/* Mobile Controls */}
            <div className="flex md:hidden items-center gap-3">

            {/* Mobile Theme Toggle — clean brand-aligned slider */}
            <button
              onClick={toggleTheme}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{
                width: 52,
                height: 28,
                borderRadius: 14,
                background: isDark
                  ? '#1f1f23'
                  : '#f3f4f6',
                border: isDark
                  ? '1px solid rgba(255,255,255,0.1)'
                  : '1px solid rgba(0,0,0,0.1)',
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.3s ease',
                padding: 0,
                overflow: 'visible',
                boxShadow: isDark
                  ? 'inset 0 2px 4px rgba(0,0,0,0.3)'
                  : 'inset 0 2px 4px rgba(0,0,0,0.05)',
                minHeight: 'unset',
                minWidth: 'unset',
                flexShrink: 0,
              }}
            >
              {/* Sliding thumb */}
              <span
                style={{
                  position: 'absolute',
                  top: 2,
                  left: isDark ? 'calc(100% - 24px - 2px)' : '2px',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: isDark ? '#dc2626' : '#fff',
                  boxShadow: isDark
                    ? '0 2px 8px rgba(220,38,38,0.4)'
                    : '0 2px 8px rgba(0,0,0,0.15)',
                  transition: 'left 0.3s cubic-bezier(0.34,1.56,0.64,1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {isDark
                  ? <Moon size={12} color="#fff" />
                  : <Sun size={12} color="#dc2626" />
                }
              </span>
            </button>

              {/* Unique Hamburger — circular design with animated morph */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMenuOpen}
                style={{
                  width: 46,
                  height: 46,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  border: 'none',
                  background: isMenuOpen
                    ? isDark ? 'rgba(220,38,38,0.2)' : 'rgba(220,38,38,0.12)'
                    : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                  boxShadow: isMenuOpen
                    ? '0 0 0 2px rgba(220,38,38,0.5), 0 6px 20px rgba(220,38,38,0.25)'
                    : isDark
                      ? '0 0 0 1px rgba(255,255,255,0.1), 0 4px 12px rgba(0,0,0,0.3)'
                      : '0 0 0 1px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.06)',
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.34,1.56,0.64,1)',
                  minHeight: 'unset',
                  minWidth: 'unset',
                  position: 'relative',
                  overflow: 'hidden',
                  flexShrink: 0,
                  padding: 0,
                }}
              >
                {/* Circular border ring */}
                <span style={{
                  position: 'absolute',
                  inset: 2,
                  borderRadius: '50%',
                  border: isMenuOpen
                    ? '2px solid rgba(220,38,38,0.6)'
                    : isDark ? '1.5px solid rgba(255,255,255,0.12)' : '1.5px solid rgba(0,0,0,0.1)',
                  transition: 'all 0.4s ease',
                  pointerEvents: 'none',
                }} />
                {/* Bar container — rotates when open */}
                <span style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 5,
                  width: 20,
                  height: 20,
                  transition: 'transform 0.45s cubic-bezier(0.4,0,0.2,1)',
                  transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                }}>
                  {/* Line 1 — full width, morphs to X */}
                  <span style={{
                    display: 'block',
                    width: 18,
                    height: 2.5,
                    borderRadius: 2,
                    background: isMenuOpen ? '#dc2626' : isDark ? '#e5e7eb' : '#374151',
                    transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1)',
                    transform: isMenuOpen ? 'translateY(7.5px) rotate(45deg)' : 'none',
                    transformOrigin: 'center',
                  }} />
                  {/* Line 2 — shorter, brand accent, fades out */}
                  <span style={{
                    display: 'block',
                    width: 11,
                    height: 2.5,
                    borderRadius: 2,
                    background: '#dc2626',
                    transition: 'all 0.25s ease',
                    opacity: isMenuOpen ? 0 : 1,
                    transform: isMenuOpen ? 'scaleX(0) rotate(90deg)' : 'none',
                    alignSelf: 'flex-start',
                  }} />
                  {/* Line 3 — full width, morphs to X */}
                  <span style={{
                    display: 'block',
                    width: 18,
                    height: 2.5,
                    borderRadius: 2,
                    background: isMenuOpen ? '#dc2626' : isDark ? '#e5e7eb' : '#374151',
                    transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1)',
                    transform: isMenuOpen ? 'translateY(-7.5px) rotate(-45deg)' : 'none',
                    transformOrigin: 'center',
                  }} />
                </span>
              </button>
            </div>

          </div>
        </div>
      </header>

      {/* Mobile Menu — curtain drop from top */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 999,
          pointerEvents: isMenuOpen ? 'all' : 'none',
        }}
        className="md:hidden"
      >
        {/* Backdrop */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.25)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            opacity: isMenuOpen ? 1 : 0,
            transition: 'opacity 0.35s ease',
          }}
          onClick={() => setIsMenuOpen(false)}
        />

        {/* Menu panel — drops from top */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            background: isDark
              ? 'linear-gradient(180deg, #060608 0%, #0e0e12 60%, #0a0a0e 100%)'
              : 'linear-gradient(180deg, #ffffff 0%, #fefefe 60%, #fef5f5 100%)',
            transform: isMenuOpen ? 'translateY(0)' : 'translateY(-105%)',
            transition: 'transform 0.45s cubic-bezier(0.4,0,0.2,1)',
            boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
            borderBottom: isDark
              ? '1px solid rgba(220,38,38,0.2)'
              : '1px solid rgba(220,38,38,0.12)',
            overflowY: 'auto',
            maxHeight: '92vh',
            paddingBottom: 28,
          }}
        >
          {/* Red accent bar at very top */}
          <div style={{
            height: 3,
            background: 'linear-gradient(90deg, transparent 0%, #dc2626 30%, #991b1b 70%, transparent 100%)',
          }} />

          {/* Push content below the fixed header (ticker + nav ≈ 96px) */}
          <div style={{ paddingTop: 96 }} />

          {/* Nav Links */}
          <nav style={{ padding: '8px 20px 20px' }}>
            {navLinks.map(({ to, label }, i) => {
              const active = location.pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setIsMenuOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '15px 18px',
                    borderRadius: 16,
                    marginBottom: 5,
                    fontSize: 16,
                    fontWeight: active ? 700 : 500,
                    color: active ? '#dc2626' : isDark ? '#e5e7eb' : '#1f2937',
                    background: active
                      ? isDark ? 'rgba(220,38,38,0.12)' : 'rgba(220,38,38,0.07)'
                      : isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.025)',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    borderLeft: `3px solid ${active ? '#dc2626' : 'transparent'}`,
                    minHeight: 'unset',
                    minWidth: 'unset',
                    letterSpacing: active ? 0.2 : 0,
                  }}
                >
                  <span>{label}</span>
                  <span style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: active
                      ? 'rgba(220,38,38,0.15)'
                      : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    flexShrink: 0,
                  }}>
                    <svg width="14" height="14" viewBox="0 0 14 14">
                      <path d="M4 2.5l5 4.5-5 4.5" stroke={active ? '#dc2626' : isDark ? '#6b7280' : '#9ca3af'} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* Divider */}
          <div style={{
            margin: '0 20px 20px',
            height: 1,
            background: isDark
              ? 'linear-gradient(90deg, transparent, rgba(220,38,38,0.2), transparent)'
              : 'linear-gradient(90deg, transparent, rgba(220,38,38,0.12), transparent)',
          }} />

          {/* Bottom section */}
          <div style={{ padding: '0 20px' }}>

            {/* Language selector */}
            <div style={{
              padding: '14px 16px',
              borderRadius: 16,
              border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.07)',
              background: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.02)',
              marginBottom: 14,
            }}>
              <div style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 2,
                color: isDark ? '#4b5563' : '#9ca3af',
                marginBottom: 10,
                textTransform: 'uppercase',
              }}>Language</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {languages.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => { changeLanguage(lang.code); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '5px 10px',
                      borderRadius: 8,
                      border: language === lang.code
                        ? '1.5px solid #dc2626'
                        : isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.07)',
                      background: language === lang.code
                        ? isDark ? 'rgba(220,38,38,0.15)' : 'rgba(220,38,38,0.08)'
                        : 'transparent',
                      color: language === lang.code ? '#dc2626' : isDark ? '#9ca3af' : '#6b7280',
                      fontSize: 12,
                      fontWeight: language === lang.code ? 700 : 400,
                      cursor: 'pointer',
                      transition: 'all 0.18s',
                      minHeight: 'unset',
                      minWidth: 'unset',
                    }}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Contact info */}
            <div style={{
              padding: '14px 16px',
              borderRadius: 16,
              background: isDark
                ? 'linear-gradient(135deg, rgba(220,38,38,0.1), rgba(153,27,27,0.06))'
                : 'linear-gradient(135deg, rgba(220,38,38,0.07), rgba(153,27,27,0.03))',
              border: '1px solid rgba(220,38,38,0.15)',
              marginBottom: 14,
            }}>
              <a href="tel:+8618013289913" style={{
                display: 'flex', alignItems: 'center', gap: 8,
                color: '#dc2626', fontSize: 13, fontWeight: 600,
                textDecoration: 'none', marginBottom: 8,
                minHeight: 'unset', minWidth: 'unset',
              }}>
                <Phone size={13} />
                +86 180 1328 9913
              </a>
              <a href="mailto:tony@qsconn.com" style={{
                display: 'flex', alignItems: 'center', gap: 8,
                color: isDark ? '#9ca3af' : '#6b7280', fontSize: 13,
                textDecoration: 'none',
                minHeight: 'unset', minWidth: 'unset',
              }}>
                <Mail size={13} />
                tony@qsconn.com
              </a>
            </div>

            {/* CTA */}
            <Link
              to="/contact"
              onClick={() => setIsMenuOpen(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '15px',
                borderRadius: 16,
                background: 'linear-gradient(135deg, #dc2626, #991b1b)',
                color: '#fff',
                fontWeight: 700,
                fontSize: 15,
                textDecoration: 'none',
                boxShadow: '0 8px 28px rgba(220,38,38,0.4)',
                letterSpacing: 0.3,
                minHeight: 'unset',
                minWidth: 'unset',
              }}
            >
              {t('getAQuote', language)}
              <svg width="16" height="16" viewBox="0 0 16 16">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes dropdownIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes mobileTicker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .nav-link-hover:hover {
          color: #dc2626 !important;
          background: rgba(220,38,38,0.07) !important;
        }
        @media (max-width: 767px) {
          body {
            padding-bottom: 62px;
          }
        }
      `}</style>
    </>
  );
};

export default Header;
